'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import QRCode from 'qrcode';
import { useTranslation } from '@/hooks/useTranslation';
import { siteAttendanceApi, type QrTokenResponse } from '@/services/api/site-attendance';

function formatClock(d: Date): string {
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  const ss = d.getSeconds().toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function formatClockAt(ms: number): string {
  return formatClock(new Date(ms));
}

export default function DisplayPage() {
  const params = useParams();
  const { t } = useTranslation();
  const s = t.siteAttendance as Record<string, string>;
  const code = String(params.code ?? '');

  const [qr, setQr] = useState<QrTokenResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const timerRef = useRef<number | null>(null);

  const loadAndRender = useCallback(async () => {
    try {
      setError(null);
      const resp = await siteAttendanceApi.getQrToken(code);
      setQr(resp);

      // Only try to load name on first load (silent failure fine)
      // SIGNED mode: /public needs t; use the QR token we just fetched
      if (!name) {
        try {
          const cp = await siteAttendanceApi.getPublicCheckpoint(
            code,
            resp.token || undefined,
          );
          setName(cp.name);
        } catch {
          setName(code);
        }
      }

      // Build the QR content URL
      const base =
        typeof window !== 'undefined' ? `${window.location.origin}` : '';
      let targetUrl: string;
      if (resp.scope === 'shared') {
        targetUrl = `${base}/siteattendance/shared/${encodeURIComponent(code)}`;
      } else {
        targetUrl = `${base}/siteattendance/c/${encodeURIComponent(code)}`;
      }
      if (resp.token) {
        targetUrl += `?t=${encodeURIComponent(resp.token)}`;
      }

      const dataUrl = await QRCode.toDataURL(targetUrl, {
        margin: 1,
        width: 512,
        errorCorrectionLevel: 'M',
      });
      setQrDataUrl(dataUrl);

      // Schedule refresh
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (resp.expiresAt != null) {
        const delay = Math.max(1000, resp.expiresAt + 1000 - Date.now());
        timerRef.current = window.setTimeout(() => {
          void loadAndRender();
        }, delay);
      }
    } catch (e) {
      setError(s?.displayServiceUnavailable ?? 'QR service unavailable');
    }
  }, [code, name, s]);

  useEffect(() => {
    void loadAndRender();
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [loadAndRender]);

  useEffect(() => {
    const i = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(i);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠</div>
          <div className="text-xl">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8">
      <div className="text-center mb-8">
        <div className="text-3xl md:text-5xl font-semibold mb-2">{name || code}</div>
        <div className="text-gray-400 text-lg md:text-xl tabular-nums">
          {formatClock(new Date(now))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-2xl">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="Check-in QR"
            className="w-[min(60vw,60vh)] h-[min(60vw,60vh)]"
          />
        ) : (
          <div className="w-[min(60vw,60vh)] h-[min(60vw,60vh)] flex items-center justify-center text-gray-400">
            …
          </div>
        )}
      </div>

      {qr?.expiresAt != null && (
        <div className="text-gray-500 text-sm mt-6">
          {s?.displayRefreshAt ?? 'Next refresh'} · {formatClockAt(qr.expiresAt)}
        </div>
      )}
    </div>
  );
}
